import { formatCurrency } from './format';
import type { SuggestionTip } from '../types';

/**
 * Render a structured `SuggestionTip` from the calculator as a human-readable
 * message. The Rust calculator emits typed variants; the frontend owns the
 * copy. Kept separate from rendering hooks so it's pure-function testable.
 *
 * The `satisfies never` guard on the default branch ensures TypeScript will
 * compile-fail if a new SuggestionTip variant is added but not handled here.
 */
export function renderSuggestionTip(tip: SuggestionTip): string {
  switch (tip.type) {
    case 'tapered_aa_exhausted':
      return "You are affected by the tapered pension annual allowance. Further pension contributions won't help, but charitable donations may reduce your threshold and adjusted income, potentially restoring some allowance.";
    case 'tapered_aa_active':
      return 'You are close to or above the tapered pension allowance limits. Consider pension or charity contributions to restore part or all of your pension annual allowance.';
    case 'child_benefit_taper_reducible':
      return `Contribute up to ~${formatCurrency(tip.contribution)} (pre-HMRC top-up) to SIPP/charity to reduce ${formatCurrency(tip.charge)} child benefit taper.`;
    case 'restore_free_childcare':
      return `Contribute ~${formatCurrency(tip.contribution)} (pre-HMRC top-up) to SIPP/charity to restore free childcare.`;
    case 'restore_personal_allowance':
      return `Contribute up to ~${formatCurrency(tip.contribution)} (pre-HMRC top-up) to SIPP/charity to restore personal allowance.`;
    case 'annual_allowance_charge':
      return `⚠️ You'll pay an annual allowance charge of ${formatCurrency(tip.charge)} because your pension contributions exceed your tapered allowance. Consider reducing contributions to avoid this charge.`;
    default: {
      // Exhaustiveness guard: any new SuggestionTip variant added in
      // calculator/src/application/suggestion.rs without a case here
      // will be caught by TypeScript at compile time.
      const _exhaustive: never = tip;
      return _exhaustive;
    }
  }
}
